import { API, ApiPrefixes } from "../api";

export const memberContributionApi = API.injectEndpoints({
  endpoints: (builder) => ({
    // Get member's contribution summary/analysis (totals only)
    // This endpoint returns just the summary values
    // (total_contributed, total_savings, total_shares, total_contribution)
    // total_contribution / total_contributed = grand total (savings + shares + other)
    // total_savings = savings kind net of withdrawals
    // total_shares = shares kind
    // Optimized for mobile apps that only need the dashboard values without fetching full history
    // Note: member_id is not needed - backend gets it from authenticated token
    getMemberContributionSummary: builder.query({
      query: () => {
        const params = new URLSearchParams({
          guard: "member",
        });

        return {
          // Members use /members/contributions (NOT /cooperatives/member_contributions)
          url: `/members/contributions/summary?${params.toString()}`,
        };
      },
      transformResponse: (responseData) => responseData?.data,
      providesTags: (result, error, arg) => [
        { type: "member-contributions-summary", id: "current" },
      ],
      cacheStrategy: "cache-and-network",
    }),

    // The member's own money per category (paid, pending, remaining, last payment) plus overall totals.
    getMemberCategoriesSummary: builder.query({
      query: () => ({
        url: `/members/contributions/categories-summary?guard=member`,
      }),
      transformResponse: (responseData) => responseData?.data,
      providesTags: [{ type: "member-contributions-summary", id: "categories" }],
      cacheStrategy: "cache-and-network",
    }),

    // Get member's own contribution history
    // Note: member_id is not needed - backend gets it from authenticated token
    getMemberContributionHistory: builder.query({
      query: ({
        page = 1,
        limit = 20,
        contribution_category_id,
        payment_type_id,
        from_date,
        to_date,
        search,
        filter_type = "all", // 'all' | 'withdrawable' | 'non-withdrawable' | 'savings' | 'shares' | 'other'
      }) => {
        const params = new URLSearchParams({
          guard: "member",
          page: page.toString(),
          limit: limit.toString(),
        });

        // Always include filter_type (backend handles "all")
        params.append("filter_type", filter_type);

        if (contribution_category_id)
          params.append("contribution_category_id", contribution_category_id);
        if (payment_type_id) params.append("payment_type_id", payment_type_id);
        if (from_date) params.append("from_date", from_date);
        if (to_date) params.append("to_date", to_date);
        if (search) params.append("search", search);

        return {
          // Members use /members/contributions (NOT /cooperatives/member_contributions)
          url: `/members/contributions/history?${params.toString()}`,
        };
      },
      transformResponse: (responseData) => responseData?.data,
      providesTags: (result, error, arg) => [
        { type: "member-contributions-history", id: "member-list" },
      ],
      cacheStrategy: "cache-and-network",
    }),

    // Get contribution categories available to members
    // Members use /members/settings/contributions_categories (not /cooperatives/settings)
    // Backend should automatically filter by the authenticated member's branch_id
    getMemberContributionCategories: builder.query({
      query: () => ({
        url: `/members/settings/contributions_categories?guard=member&page=1&limit=100`,
      }),
      transformResponse: (responseData) => {
        // Debug logging to help identify backend issues
        if (process.env.NODE_ENV === "development") {
          console.log("Member Contribution Categories Response:", responseData);
        }
        return responseData?.data;
      },
      providesTags: (result, error, arg) => [
        { type: "member-contribution-categories", id: arg },
      ],
      cacheStrategy: "cache-and-network",
    }),

    // Create a contribution for the logged-in member
    // Supports multiple contributions in one request (like staff)
    // CRITICAL: Members use /members/contributions (NOT /cooperatives/member_contributions)
    // This is different from staff endpoints which use /cooperatives/member_contributions
    // Using unique endpoint name to avoid conflict with admin contribution API
    createMemberOwnContribution: builder.mutation({
      query: (body) => {
        // Members have SEPARATE endpoint path: /members/contributions (not /cooperatives/member_contributions)
        // Force guard=member to prevent any caching issues
        const url = `/members/contributions/create?guard=member`;
        console.log("🔵 MEMBER Contribution API Call:", {
          url,
          body,
          endpoint: "createMemberOwnContribution",
          guard: "member",
        });
        return {
          url: url,
          method: "POST",
          body: body,
        };
      },
      invalidatesTags: [
        { type: "member-contributions-history" },
        { type: "member-contributions-summary" },
        { type: "member-contribution-categories" },
        { type: "member-online-pending-payments" },
        { type: "online-pending-payments" },
        "StaffDashboard",
      ],
    }),

    // Get payment methods for members (Paystack only)
    getMemberPaymentMethods: builder.query({
      query: () => ({
        url:
          ApiPrefixes["generalSelectables"] + `/payment_methods?guard=member`,
      }),
      transformResponse: (responseData) => responseData?.data,
    }),

    // Get member's saved/tokenized cards
    getMemberSavedCards: builder.query({
      query: () => ({
        url: `/members/payment-cards?guard=member`,
      }),
      transformResponse: (responseData) => responseData?.data || [],
      providesTags: (result, error, arg) => [
        { type: "member-saved-cards", id: "list" },
      ],
    }),

    // Get contribution breakdown by category for member
    getMemberContributionBreakdown: builder.query({
      query: ({ categoryId, page = 1, member_id }) => ({
        // Members use /members/contributions (NOT /cooperatives/member_contributions)
        url: `/members/contributions/${categoryId}/breakdown?guard=member&member_id=${member_id}&page=${page}&limit=20`,
      }),
      transformResponse: (responseData) => responseData?.data,
      providesTags: (result, error, arg) => [
        { type: "member-contribution-breakdown", id: arg?.categoryId },
      ],
      cacheStrategy: "cache-and-network",
    }),

    // Search contribution records for member
    searchMemberContributionRecords: builder.query({
      query: ({ search, page = 1, member_id }) => ({
        // Members use /members/contributions (NOT /cooperatives/member_contributions)
        url: `/members/contributions/search?guard=member&member_id=${member_id}&page=${page}&limit=20&search=${search}`,
      }),
      transformResponse: (responseData) => responseData?.data,
    }),

    // Verify payment status for member payments (authenticated)
    verifyPaymentStatus: builder.query({
      query: ({ reference }) => ({
        url: `/members/payments/verify?reference=${reference}&guard=member`,
      }),
      transformResponse: (responseData) => responseData?.data,
    }),

    // Public payment verification (no authentication required)
    // Used for payment confirmation pages after Paystack redirect
    // Mobile apps redirect to web confirmation page, which needs to verify payment without login
    verifyPaymentStatusPublic: builder.query({
      query: ({ reference }) => ({
        url: `/public/payments/verify?reference=${reference}`,
      }),
      transformResponse: (responseData) => responseData?.data,
    }),

    getMemberAvailableRails: builder.query({
      query: () => ({
        url: `/members/payments/available-rails?guard=member`,
      }),
      transformResponse: (responseData) => responseData?.data,
    }),

    getMemberPaymentFeePreview: builder.query({
      query: ({ principal_amount, payment_channel }) => {
        let url = `/members/payments/fee-preview?guard=member&principal_amount=${principal_amount}`;
        if (payment_channel) {
          url += `&payment_channel=${encodeURIComponent(payment_channel)}`;
        }
        return { url };
      },
      transformResponse: (responseData) => responseData?.data,
    }),

    getMemberCompanyBanks: builder.query({
      query: () => ({
        url: `/members/selectables/company_banks?guard=member`,
      }),
      transformResponse: (responseData) => responseData?.data ?? [],
    }),

    createMemberContributionBankTransfer: builder.mutation({
      query: (body) => ({
        url: `/members/contributions/create-bank-transfer?guard=member`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "member-contributions-history" },
        { type: "member-contributions-summary" },
        { type: "member-contribution-categories" },
        { type: "member-bank-transfer-payments" },
      ],
    }),

    getMemberBankTransferPayments: builder.query({
      query: ({ page = 1, limit = 20 }) => ({
        url: `/members/payments/bank-transfer?guard=member&page=${page}&limit=${limit}`,
      }),
      transformResponse: (responseData) => responseData?.data,
      providesTags: [{ type: "member-bank-transfer-payments", id: "list" }],
    }),

    getMemberOnlinePendingPayments: builder.query({
      query: ({ status = "pending", page = 1, limit = 20 } = {}) => ({
        url: `/members/payments/online-pending?guard=member&status=${status}&page=${page}&limit=${limit}`,
      }),
      transformResponse: (responseData) => responseData?.data,
      providesTags: [{ type: "member-online-pending-payments", id: "list" }],
    }),

    uploadMemberBankTransferReceipt: builder.mutation({
      query: ({ pendingId, receiptUrl }) => {
        return {
          url: `/members/payments/bank-transfer/${pendingId}/receipt?guard=member`,
          method: "POST",
          body: { receipt_url: receiptUrl },
        };
      },
      invalidatesTags: [{ type: "member-bank-transfer-payments", id: "list" }],
    }),

    getPublicPaymentFeePreview: builder.query({
      query: ({ company_id, principal_amount }) => ({
        url: `/public/payments/fee-preview?company_id=${company_id}&principal_amount=${principal_amount}`,
      }),
      transformResponse: (responseData) => responseData?.data,
    }),

    // Manually verify payment with the originating gateway (Paystack or ALATPay)
    manuallyVerifyPayment: builder.mutation({
      query: ({ reference }) => ({
        url: `/members/payments/verify-manual?reference=${reference}&guard=member`,
        method: "POST",
      }),
      transformResponse: (responseData) => responseData?.data,
    }),

    // Public manual payment verification (no authentication required)
    // Backend routes to Paystack or ALATPay based on pending payment provider
    manuallyVerifyPaymentPublic: builder.mutation({
      query: ({ reference, transaction_id, alatpay_transaction_id }) => {
        const params = new URLSearchParams({ reference: String(reference) });
        const txnId = transaction_id || alatpay_transaction_id;
        if (txnId) {
          params.set("transaction_id", String(txnId));
        }
        return {
          url: `/public/payments/verify-manual?${params.toString()}`,
          method: "POST",
          body: txnId
            ? { transaction_id: String(txnId), alatpay_transaction_id: String(txnId) }
            : undefined,
        };
      },
      transformResponse: (responseData) => responseData?.data,
    }),

    getMyOpeningBalanceCorrections: builder.query({
      query: ({ page = 1, limit = 20 }) => ({
        url: `/members/opening_balance_corrections?guard=member&page=${page}&limit=${limit}`,
      }),
      transformResponse: (responseData) => responseData?.data,
      providesTags: ["member-opening-balance-corrections"],
    }),
  }),
});

export const {
  useGetMemberContributionSummaryQuery,
  useGetMemberContributionHistoryQuery,
  useGetMemberContributionCategoriesQuery,
  useCreateMemberOwnContributionMutation,
  useGetMemberPaymentMethodsQuery,
  useGetMemberSavedCardsQuery,
  useGetMemberCategoriesSummaryQuery,
  useGetMemberContributionBreakdownQuery,
  useSearchMemberContributionRecordsQuery,
  useVerifyPaymentStatusQuery,
  useVerifyPaymentStatusPublicQuery,
  useGetMemberAvailableRailsQuery,
  useGetMemberPaymentFeePreviewQuery,
  useGetMemberCompanyBanksQuery,
  useCreateMemberContributionBankTransferMutation,
  useGetMemberBankTransferPaymentsQuery,
  useGetMemberOnlinePendingPaymentsQuery,
  useUploadMemberBankTransferReceiptMutation,
  useGetPublicPaymentFeePreviewQuery,
  useManuallyVerifyPaymentMutation,
  useManuallyVerifyPaymentPublicMutation,
  useGetMyOpeningBalanceCorrectionsQuery,
} = memberContributionApi;
